// uiarc: breadcrumb (https://uiarc.dev/r/breadcrumb.json) — shadcn parçaları (Breadcrumb/List/Item/Link/Page/
// Separator) üzerinde. Davranış uiarc ile aynı: ilk çizimdeki kırıntılar yerinde durur, sonradan eklenen yoldan
// sola doğru kayarak gelir (bulanıklıktan netleşir), çıkan kırıntı kısa solar; kalanlar yerleşimi yayla izler.
// Bu animasyon için `BreadcrumbItem`/`BreadcrumbSeparator` anahtarlı (`key`) verilmelidir: kırıntı adı değişince
// eski çıkar, yenisi girer. Her kırıntı koyu (500) etiketinin genişliğini ayırır; geçerli sayfa olunca yol kaymaz.
// `prefers-reduced-motion` altında hareket yok.
import { ChevronRight } from 'lucide-react'
import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import type { HTMLMotionProps } from 'motion/react'
import * as React from 'react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

/** Bağlantı/geçerli sayfa kutusu: etiketin koyu halinin genişliğini `::after` ile ayırır, uzun etiket `truncate` ile kısalır. */
const crumb =
  'inline-flex min-w-0 flex-col rounded-[7px] px-0.5 py-1 after:invisible after:h-0 after:overflow-hidden after:font-medium after:content-[attr(data-label)] after:pointer-events-none after:select-none'

function Breadcrumb(props: React.ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />
}

function BreadcrumbList({ className, children, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        'relative m-0 flex list-none flex-wrap items-center gap-x-1.5 gap-y-0.5 p-0 break-words',
        className,
      )}
      {...props}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {React.Children.toArray(children)}
      </AnimatePresence>
    </ol>
  )
}

type CrumbMotionProps = Omit<
  HTMLMotionProps<'li'>,
  'initial' | 'animate' | 'exit' | 'transition' | 'layout' | 'children'
> & { children?: React.ReactNode }

/** Çıkış/giriş: kırıntı yoldan sola doğru gelir; çıkan kısa solar. */
function useCrumbMotion() {
  const reduced = useReducedMotion() ?? false
  const still = { duration: 0 }
  return {
    layout: reduced ? (false as const) : ('position' as const),
    initial: reduced
      ? (false as const)
      : { opacity: 0, x: -8, filter: `blur(${motionTokens.blur.subtle}px)` },
    animate: { opacity: 1, x: 0, filter: 'blur(0px)' },
    exit: reduced
      ? { opacity: 0, transition: still }
      : {
          opacity: 0,
          x: -4,
          filter: `blur(${motionTokens.blur.subtle}px)`,
          transition: { duration: motionTokens.duration.instant, ease: motionTokens.ease.standard },
        },
    transition: reduced
      ? still
      : {
          duration: motionTokens.duration.standard,
          ease: motionTokens.ease.enter,
          layout: motionTokens.spring.smooth,
        },
  }
}

function BreadcrumbItem({
  className,
  ref,
  ...props
}: CrumbMotionProps & { ref?: React.Ref<HTMLLIElement> }) {
  const crumbMotion = useCrumbMotion()
  return (
    <motion.li
      ref={ref}
      data-slot="breadcrumb-item"
      className={cn(
        'text-text-muted leading-body inline-flex items-center gap-1.5 text-sm whitespace-nowrap',
        className,
      )}
      {...crumbMotion}
      {...props}
    />
  )
}

/** Geçerli sayfa olmayan kırıntı: bağlantı (varsayılan `<a>`) ya da `render` ile düğme/yönlendirici bağlantı. */
function BreadcrumbLink({ className, render, children, ...props }: useRender.ComponentProps<'a'>) {
  return useRender({
    defaultTagName: 'a',
    props: mergeProps<'a'>(
      {
        className: cn(
          crumb,
          'text-text-secondary ease-standard cursor-pointer [-webkit-tap-highlight-color:transparent] underline decoration-transparent decoration-1 underline-offset-4 transition-[color,text-decoration-color] duration-(--duration-fast) hover:text-brand-strong hover:decoration-[color-mix(in_oklab,currentColor_45%,transparent)] motion-reduce:transition-none',
          className,
        ),
        'data-label': typeof children === 'string' ? children : undefined,
        children:
          typeof children === 'string' ? <span className="truncate">{children}</span> : children,
      } as React.ComponentProps<'a'>,
      props,
    ),
    render,
    state: { slot: 'breadcrumb-link' },
  })
}

function BreadcrumbPage({ className, children, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-page"
      aria-current="page"
      data-label={typeof children === 'string' ? children : undefined}
      className={cn(
        crumb,
        'text-brand-strong ease-standard font-medium transition-[color] duration-(--duration-fast) motion-reduce:transition-none',
        className,
      )}
      {...props}
    >
      {typeof children === 'string' ? <span className="truncate">{children}</span> : children}
    </span>
  )
}

function BreadcrumbSeparator({
  className,
  ref,
  children,
  ...props
}: CrumbMotionProps & { ref?: React.Ref<HTMLLIElement> }) {
  const crumbMotion = useCrumbMotion()
  return (
    <motion.li
      ref={ref}
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn(
        'text-border-strong inline-flex items-center [&>svg]:size-4 [&>svg]:flex-none [&>svg]:stroke-[1.75]',
        className,
      )}
      {...crumbMotion}
      {...props}
    >
      {children ?? <ChevronRight />}
    </motion.li>
  )
}

export {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
}
